import { NoteFlagBadge, NoteFlagChip, type NoteFlag } from '@app/features/visits/noteFlag';
import { visitEditPath } from '@app/features/dashboard/visitLinks';
import { getDayjsLocaleByLanguage } from '@app/i18n/helpers';
import dayjs from '@app/utils/dayjs-setup';
import { LIST_DATE_FORMAT, LIST_TIME_FORMAT } from '@app/utils/dates/dateFormats';
import { Alert, Skeleton } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink, FiUser } from 'react-icons/fi';

import { NOTE_ROLE_LABEL_KEYS, recipientsBesidesAuthor } from './noteAudience';
import { Note, NoteRole } from './notes.types';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

/**
 * A note's instant, with the year.
 *
 * The year is not optional: a client's stream runs for as long as the office has
 * had them, so one screen can hold two Februaries. Only calendar chrome under a
 * period label is allowed to drop it, and this is not that.
 */
export const formatNoteWhen = (instant: string, language: string): string => {
  const moment = dayjs(instant).locale(getDayjsLocaleByLanguage(language));
  if (!moment.isValid()) return '';
  return `${moment.format(LIST_DATE_FORMAT)} ${moment.format(LIST_TIME_FORMAT)}`;
};

/**
 * Up to two letters from the author's name, for the avatar.
 *
 * Empty when there is no name: a migrated note legitimately has none, and initials
 * made up from a role would put a person on a note nobody signed.
 */
export const noteAuthorInitials = (name: string | null | undefined): string => {
  if (!name) return '';
  const letters = name
    .split(/\s+/)
    .map((part) => Array.from(part).find((character) => /[\p{L}\p{N}]/u.test(character)))
    .filter((character): character is string => !!character);
  return letters.slice(0, 2).join('').toUpperCase();
};

/** Newest first, tie-broken by id — the same order the endpoint pages by. */
export const sortNotesNewestFirst = (notes: readonly Note[]): Note[] =>
  [...notes].sort((left, right) => {
    const delta = inDisplayZone(right.createdAt).valueOf() - inDisplayZone(left.createdAt).valueOf();
    if (delta !== 0 && !Number.isNaN(delta)) return delta;
    return right.noteId - left.noteId;
  });

type NoteRowProps = {
  note: Note;
  /** Off inside a visit's own panel, where every note is on the visit already. */
  showVisitLink?: boolean;
  /**
   * Given, the flag becomes a control on this row: the office classifies a note it did
   * not write, which is the one field someone other than the author may set. Absent,
   * the flag is read as a badge.
   */
  onFlagChange?: (flag: NoteFlag | null) => void;
  /** True while this row's flag change is in flight. */
  isSavingFlag?: boolean;
};

export const NoteRow: React.FC<NoteRowProps> = ({ note, showVisitLink, onFlagChange, isSavingFlag }) => {
  const { t, i18n } = useTranslation();

  // No role, no name: the note is shown, unattributed. Naming the office as the
  // author of something a caregiver or a doctor wrote is worse than saying nothing.
  const roleLabel = note.authorRole ? t(NOTE_ROLE_LABEL_KEYS[note.authorRole]) : null;

  // The attribution is one string per locale, so each language can place the name
  // itself. Greek carries a label ("Από: Γραφείο") rather than a verb, which would
  // need an article that changes with each role's gender.
  const author = note.authorName ?? roleLabel;
  const attribution = author ? t('notes-author-attribution', { author }) : null;

  const listRoles = (roles: readonly NoteRole[]) => roles.map((role) => t(NOTE_ROLE_LABEL_KEYS[role])).join(', ');

  // Who the note was sent to is the audience less whoever wrote it. Without an author
  // role there is no sender to subtract, so the caption says who can read the note
  // rather than claiming someone sent it somewhere.
  let audienceLabel = t('notes-audience-unknown');
  if (note.audience !== null) {
    const recipients = note.authorRole ? recipientsBesidesAuthor(note.audience, note.authorRole) : note.audience;
    const summaryKey = note.authorRole ? 'notes-audience-summary' : 'notes-audience-readers';
    audienceLabel =
      recipients.length === 0 ? t('notes-audience-internal') : t(summaryKey, { roles: listRoles(recipients) });
  }

  const initials = noteAuthorInitials(note.authorName);

  return (
    <article className="flex items-start gap-3 rounded-xl border border-slate-200/70 bg-slate-50/50 px-3.5 py-3">
      {/* Decorative: the header beside it already says who wrote the note, and an
          unsigned note gets the neutral mark rather than initials for a made-up person. */}
      <div
        aria-hidden="true"
        className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200"
      >
        {initials || <FiUser className="text-sm text-slate-400" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* The header reads as attribution rather than as a label: a signed note names
              the person and carries their role beside it, an unsigned one names the role
              alone, and a note with neither says so instead of guessing. */}
          {attribution ? (
            <span className="truncate text-[13px] font-semibold text-slate-800">{attribution}</span>
          ) : (
            <span className="text-[13px] italic text-slate-400">{t('notes-author-unknown')}</span>
          )}
          {note.authorName && roleLabel && (
            <span className="flex-none rounded bg-slate-200/60 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
              {roleLabel}
            </span>
          )}
          {!note.isRead && (
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500"
              aria-label={t('notes-unread-marker')}
            />
          )}
          <span className="flex-none text-[11px] tabular-nums text-slate-400">
            {formatNoteWhen(note.createdAt, i18n.language)}
          </span>

          <span className="ml-auto flex flex-none items-center gap-1">
            {onFlagChange ? (
              <NoteFlagChip value={note.flag} onChange={onFlagChange} saving={isSavingFlag} />
            ) : (
              <NoteFlagBadge flag={note.flag} />
            )}
          </span>
        </div>

        <div className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">{note.body}</div>

        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-400">
          <span>{audienceLabel}</span>
          {note.updatedAt && <span>{t('notes-edited')}</span>}

          {/* A labelled anchor with `target`, as everywhere else that links out of a list: a
              note that belongs to a visit says so, and the reader keeps their place in the
              stream. It also gets middle-click and the browser's own menu for free. */}
          {showVisitLink && note.visitId ? (
            <a
              href={visitEditPath(note.visitId)}
              target="_blank"
              rel="noopener noreferrer"
              // `!`: antd's own anchor rule out-specifies a bare utility, so the link
              // computed black. The same `!text-app-primary-600` as the scope list.
              className="inline-flex items-center gap-1 font-medium !text-app-primary-600 underline-offset-2 hover:underline"
            >
              {t('notes-open-visit')}
              <FiExternalLink aria-hidden="true" className="text-[11px]" />
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
};

type NotesTimelineProps = {
  notes: readonly Note[];
  isLoading?: boolean;
  hasError?: boolean;
  /** Shown when there is nothing to show and nothing went wrong. */
  emptyKey?: string;
  showVisitLinks?: boolean;
  maxHeight?: number;
  /** Fill the height the parent gives instead of capping at `maxHeight`, so a panel
      beside another can match it without a band of empty card. */
  fill?: boolean;
  /** Given, every row's flag becomes a control. See `NoteRow`. */
  onFlagChange?: (noteId: number, flag: NoteFlag | null) => void;
  /** The one row whose flag change is in flight, if any. */
  savingFlagNoteId?: number | null;
};

/**
 * A client's or a visit's notes, newest first.
 *
 * It renders an error as an error and an empty stream as an empty stream, and never
 * a spinner that does not end: the endpoints behind it are not deployed, so the
 * failing path is the one this component spends most of its life on.
 */
const NotesTimeline: React.FC<NotesTimelineProps> = ({
  notes,
  isLoading,
  hasError,
  emptyKey = 'notes-empty',
  showVisitLinks = true,
  maxHeight = 420,
  fill = false,
  onFlagChange,
  savingFlagNoteId = null,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="p-4">
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="p-4">
        <Alert type="error" showIcon message={t('notes-load-error')} />
      </div>
    );
  }

  const ordered = sortNotesNewestFirst(notes);

  if (ordered.length === 0) {
    return <div className="px-4 py-10 text-center text-[13px] text-slate-400">{t(emptyKey)}</div>;
  }

  return (
    <div
      className={fill ? 'min-h-0 flex-1 overflow-y-auto' : 'overflow-y-auto'}
      style={fill ? undefined : { maxHeight }}
    >
      <div className="flex flex-col gap-2.5 p-3">
        {ordered.map((note) => (
          <NoteRow
            key={note.noteId}
            note={note}
            showVisitLink={showVisitLinks}
            onFlagChange={onFlagChange ? (flag) => onFlagChange(note.noteId, flag) : undefined}
            isSavingFlag={savingFlagNoteId === note.noteId}
          />
        ))}
      </div>
    </div>
  );
};

export default NotesTimeline;
